'use client';
// import { scan } from "react-scan";
import Avo, {
  CountryValueType,
  LanguageValueType,
  PreviousCategoryValueType,
  SelectedCategoryValueType,
} from '@susu/custom-made/Avo';
import type { Item } from '@susu/custom-made/components/navigation/navigation';
import { initializeAnalytics, setCustomMadePageContext } from '@susu/custom-made/analytics';
import {
  ClientType,
  ConfiguratorOriginsType,
  Clients,
  ConfiguratorOrigin,
  Level,
} from '@susu/custom-made/utils/constants';
import type {
  BusinessRules,
  DefaultValues,
  FabricFilter,
  LayerDefinitions,
  Option,
  OptionConfigurationType,
  StylePresets,
  typeFabricList,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import { initializeDataDog } from '@susu/custom-made/utils/monitoring/dataDog';
import { changeSection, getConfigurationTypeBySection, Section } from '@susu/custom-made/utils/navigation';
import type { ProductType } from '@susu/custom-made/utils/productDefinition';
import { getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId } from '@susu/custom-made/utils/productDefinition';
import {
  camelToSnakeCase,
  isExtraConfigurationSection,
  isFalsy,
  normalizeEnvironment,
  processPreFilterFormat,
  validatePreFilters,
} from '@susu/custom-made/utils/utils';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FabricPanel } from '../fabricPanel/fabricPanel';
import { OnBoardingContainer } from '../onBoarding/onBoardingContainer/onBoardingContainer';
import { OptionPanel } from '../optionPanel/optionPanel';
import type { PreviewProps } from '../preview/preview';
import PreviewHeader from '../previewHeader/previewHeader';
import { SectionLayout } from './sectionLayout';
import { ExtraProductHeader, SectionNavigation } from './sectionNavigation';
import { ConfiguratorModals, ConfiguratorToast, MissingOptionsOverlay } from './sectionOverlays';
import { SelectionEffects } from './selectionEffects';
import { useFirstRenderRef, useIsFirstRender } from './useFirstRender';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import useSizePassportContext from '@susu/custom-made/providers/hooks/useSizePassportContext';
import useGlobalContext from '@susu/custom-made/providers/hooks/useGlobalContext';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import Skeleton from '../skeleton/skeleton';
import { useEnvironmentContext } from '@susu/custom-made/providers/hooks/useEnvironmentContext';
import useQueryParams from '@susu/custom-made/hooks/useQueryParams';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { useAvo } from '@susu/custom-made/hooks/useAvo';
import { useFabricViewMode } from '@susu/custom-made/hooks/useFabricViewMode';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';

const { SHOW_ONBOARDING_PANEL, ENABLE_AVO_TRACKING } = FEATURE_TOGGLES;

const loadSummary = () => import('../summary/summary');
const Summary = lazy(loadSummary);

// Warm the Summary chunk on the client so the Suspense fallback inside
// SectionContainer doesn't flash between the outer feature-flags skeleton and
// the Summary page when navigating to (or landing on) the summary section.
if (typeof window !== 'undefined') {
  void loadSummary();
}

export interface SectionContainerProps {
  fabricList: typeFabricList;
  fabricSequence: number[];
  fabricFilters: FabricFilter[];
  fabricPreFilters: string;
  stylePresets: StylePresets;
  businessRules: BusinessRules;
  options: OptionConfigurationType;
  optionValues: typeOptionValue;
  defaultValues: DefaultValues;
  layerDefinitions: LayerDefinitions;
  datadogClientToken: string;
  datadogApplicationId: string;
  countryCode: string;
  client: ClientType;
  product: ProductType;
  origin: ConfiguratorOriginsType;
  defaultLayers: Map<string, PreviewProps>;
  defaultSection: Section;
  isInPreloadFlow: boolean;
  isCustomizeFlow: boolean;
  currentDate?: Date;
}

export const SectionContainer = ({
  fabricList,
  fabricSequence,
  fabricFilters,
  fabricPreFilters,
  stylePresets,
  businessRules,
  options,
  optionValues,
  defaultValues,
  layerDefinitions,
  datadogClientToken,
  datadogApplicationId,
  countryCode,
  client,
  product,
  origin,
  defaultLayers,
  defaultSection,
  isInPreloadFlow,
  isCustomizeFlow,
  currentDate,
}: SectionContainerProps) => {
  const { section, level, setSection, setLevel } = useNavigationContext();
  const { getQueryParams } = useQueryParams();
  const currentSection = section ?? defaultSection;
  const firstRenderRef = useFirstRenderRef();
  const isFirstRender = useIsFirstRender(firstRenderRef);
  const {
    env,
    cdnDomain,
    datadogAppVersion,
    datadogSessionSampleRate,
    datadogSessionReplaySampleRate,
    datadogLogsSampleRate,
  } = useEnvironmentContext();
  const [preFilters, setPreFilters] = useState<Record<string, string[]> | null>(null);
  const hasTrackedPageLoad = useRef(false);
  const pageLoadTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const tokenReceivedRef = useRef(false);
  const datadogCleanupRef = useRef<(() => void) | null>(null);
  const { token, setToken, setTokenInjectionFailed } = useSizePassportContext();
  const { cmCorrelationId } = useGlobalContext();
  const { language, accountNr, channelId, storeID } = useUserContext();
  const showOnboardingPanelFeatureEnabled = useFeatureIsOn(SHOW_ONBOARDING_PANEL);
  const isAvoTrackingEnabled = useFeatureIsOn(ENABLE_AVO_TRACKING);
  const { getAvoStandardEventParams } = useAvo();
  const { fabricViewMode, chooseViewMode, onModelImageLoaded, onModelImageError } = useFabricViewMode({
    origin,
    section,
  });
  const [isDatadogReady, setIsDatadogReady] = useState(false);

  const calculateSection = useCallback(
    (section: string | null, level: string | null) => {
      const toSection = section ?? Section.Fabric;
      const toLevel: Level = (level as Level) ?? Level.Group;
      changeSection(toSection, toLevel, setSection, setLevel);
    },
    [setSection, setLevel],
  );

  const matchingProductPresets = useMemo(() => {
    if (!section) {
      return [];
    }
    // To handle extra configurations which have different configuration type id but using definitions from base product
    const configurationTypeId = getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(
      +getConfigurationTypeBySection(section),
    );
    if (!configurationTypeId || !stylePresets.configurationTypes[configurationTypeId]) {
      return [];
    }

    return stylePresets.configurationTypes[configurationTypeId].styles;
  }, [section, stylePresets]);

  const updateSection = useCallback(
    (item: Item | Option) => {
      const tmpItem = item as Item;

      calculateSection(tmpItem!.id, Level.Group);

      window.DD_RUM?.addAction('Custom made - Section Changed', {
        customData: { from: section, to: tmpItem!.id },
      });

      if (isAvoTrackingEnabled) {
        Avo.customMadeCategoryChanged({
          ...getAvoStandardEventParams(`${section}${section !== Section.Fabric ? '_options' : ''}`),
          previousCategory: camelToSnakeCase(section!) as PreviousCategoryValueType,
          selectedCategory: camelToSnakeCase(tmpItem!.id) as SelectedCategoryValueType,
        });
      }
    },
    [calculateSection, isAvoTrackingEnabled, getAvoStandardEventParams, section],
  );

  const goToSummary = useCallback(() => {
    changeSection(Section.Summary, Level.Group, setSection, setLevel);
    if (isAvoTrackingEnabled) {
      Avo.custommadeFinished({
        ...getAvoStandardEventParams(`${section}${section !== Section.Fabric ? '_options' : ''}`),
      });
    }
  }, [section, setSection, setLevel, isAvoTrackingEnabled, getAvoStandardEventParams]);

  const popStateEvent = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      const params = event.target.location.search.replace('?', '').split('&');

      if (params.length) {
        let section = null;
        let level = null;
        params.forEach((param: string) => {
          if (param.split('=')[0] === 'section') {
            section = param.split('=')[1];
          }
          if (param.split('=')[0] === 'level') {
            level = param.split('=')[1];
          }
        });

        if (section || level) {
          calculateSection(section, level);
        }
      }
    },
    [calculateSection],
  );

  // To set default section and level in URL on initial load
  useEffect(() => {
    const setDefaultSection = () => {
      if (!isFirstRender) {
        return;
      }

      const queryParams = getQueryParams();

      if (queryParams) {
        calculateSection(defaultSection, level);
      }
    };

    setDefaultSection();
  }, [isFirstRender]);

  useEffect(() => {
    // Enable this to run react scan
    // scan({log: false});

    // To log the current date/time for cache
    if (currentDate) {
      console.log('currentDateServer', currentDate.toLocaleString());
      console.log('currentDateClient', new Date().toLocaleString());
    }

    // Token injection with retry — the parent may not have its message listener
    // registered yet when the iframe fires (observed in slow network environments).
    // We keep sending configurators:loaded until the parent responds.
    let retryIntervalId: ReturnType<typeof setInterval> | undefined;
    let handleTokenInjection: ((e: MessageEvent) => void) | undefined;

    if (client === Clients.inStore && isFalsy(token)) {
      handleTokenInjection = (e: MessageEvent) => {
        if (e.data.type !== 'configurators:tokenInjection') return;
        const tokenTmp = e.data.payload;
        if (!isFalsy(tokenTmp)) {
          tokenReceivedRef.current = true;
          setToken(tokenTmp);
        }
      };

      window.addEventListener('message', handleTokenInjection);

      const MAX_TOKEN_RETRIES = 50; // 25 seconds at 500ms intervals
      let attempts = 0;
      const sendConfiguratorLoaded = () => {
        if (tokenReceivedRef.current) {
          clearInterval(retryIntervalId);
          return;
        }
        if (attempts >= MAX_TOKEN_RETRIES) {
          clearInterval(retryIntervalId);
          setTokenInjectionFailed(true);
          console.warn(`[custom-made] Token injection failed: parent did not respond after ${MAX_TOKEN_RETRIES} retries (~${(MAX_TOKEN_RETRIES * 500) / 1000}s). client=${client}`);
          return;
        }
        attempts += 1;
        window.parent.postMessage({ type: 'configurators:loaded' }, '*');
      };

      sendConfiguratorLoaded();
      retryIntervalId = setInterval(sendConfiguratorLoaded, 500);
    }

    // To set and cleanup pop state event listener
    window.addEventListener('popstate', popStateEvent, false);

    // To initialize datadog
    let cancelled = false;
    initializeDataDog({
      applicationId: datadogApplicationId,
      clientToken: datadogClientToken,
      env,
      country: countryCode,
      cmCorrelationId,
      client,
      version: datadogAppVersion,
      sessionSampleRate: datadogSessionSampleRate,
      sessionReplaySampleRate: datadogSessionReplaySampleRate,
      logsSampleRate: datadogLogsSampleRate,
      channelId,
      storeID,
    }).then((cleanup) => {
      if (cancelled) {
        cleanup?.();
        return;
      }
      datadogCleanupRef.current = cleanup ?? null;
      setIsDatadogReady(true);
    }).catch((error: unknown) => {
      console.error('[DD] initializeDataDog failed:', error);
    });

    // To initialize analytics
    if (isAvoTrackingEnabled) {
      initializeAnalytics({
        client,
        environment: normalizeEnvironment(env),
        version: datadogAppVersion,
        language,
        countryCode,
        accountNr,
      });
    }

    return () => {
      cancelled = true;
      datadogCleanupRef.current?.();
      datadogCleanupRef.current = null;
      window.removeEventListener('popstate', popStateEvent, false);
      if (handleTokenInjection) {
        window.removeEventListener('message', handleTokenInjection);
      }
      clearInterval(retryIntervalId);
    };
  }, [client, env, countryCode, cmCorrelationId, channelId, storeID, datadogAppVersion, datadogSessionSampleRate, datadogSessionReplaySampleRate, datadogLogsSampleRate]);

  // The page's locale and in-store customer, for every event of the page. Runs after the
  // initialization above, and again when the in-store advisor switches customer.
  useEffect(() => {
    setCustomMadePageContext({ client, language, countryCode, accountNr });
  }, [client, language, countryCode, accountNr]);

  useEffect(() => {
    if (fabricPreFilters) {
      const preFilters = processPreFilterFormat(fabricPreFilters);
      const validatedPreFilters = validatePreFilters(preFilters, fabricFilters);
      if (Object.keys(validatedPreFilters).length) {
        setPreFilters(validatedPreFilters);
      }
    }
  }, [fabricPreFilters, fabricFilters]);

  useEffect(() => {
    if (hasTrackedPageLoad.current) return;

    if (isAvoTrackingEnabled && product && isFirstRender) {
      // Load Analytics takes time to load, so we need to wait for it to load before sending events
      pageLoadTimeoutRef.current = setTimeout(() => {
        Avo.pageLoadedCm({
          ...getAvoStandardEventParams(section!),
          localeVisited: `${language as LanguageValueType}-${countryCode.toLowerCase() as CountryValueType}`,
          customDestinationPageName_: '',
        });
      }, 1000);
      hasTrackedPageLoad.current = true;
    }
  }, [isAvoTrackingEnabled, product, section, countryCode, language, accountNr, token, isFirstRender]);

  // Cleared on unmount only: clearing on a dep change would drop the event, since the guard above stops a retry.
  useEffect(() => () => clearTimeout(pageLoadTimeoutRef.current), []);

  // To trigger page loaded event for automation testing
  useEffect(() => {
    document.dispatchEvent(new Event('cm-automation: page-loaded', { bubbles: true }));
  }, []);

  const navigation = useMemo(
    () =>
      isExtraConfigurationSection(currentSection as Section) ? undefined : (
        <SectionNavigation
          currentSection={currentSection}
          onItemClick={updateSection}
          onFinishClick={goToSummary}
        />
      ),
    [currentSection, updateSection, goToSummary],
  );
  const extraProductHeader = useMemo(
    () => <ExtraProductHeader currentSection={currentSection} onFinishClick={goToSummary} />,
    [currentSection, goToSummary],
  );

  return (
    <>
      <SelectionEffects
        options={options}
        optionValues={optionValues}
        businessRules={businessRules}
        matchingProductPresets={matchingProductPresets}
        isInPreloadFlow={isInPreloadFlow}
        isDatadogReady={isDatadogReady}
      />
      <MissingOptionsOverlay options={options} optionValues={optionValues} />
      {!isCustomizeFlow &&
        product === 'Suit' &&
        client === 'onLine' &&
        origin === ConfiguratorOrigin.Normal &&
        showOnboardingPanelFeatureEnabled && <OnBoardingContainer />}
      {currentSection && (
        <SectionLayout
          currentSection={currentSection}
          preview={
            <PreviewHeader
              layerDefinitions={layerDefinitions}
              optionValues={optionValues}
              product={product}
              stylePresets={stylePresets}
              firstRenderRef={firstRenderRef}
              defaultLayers={defaultLayers}
              isExtraProduct={isExtraConfigurationSection(currentSection as Section)}
              fabricViewMode={fabricViewMode}
              onViewModeChange={chooseViewMode}
              onModelImageLoaded={onModelImageLoaded}
              onModelImageError={onModelImageError}
              onFinishClick={goToSummary}
            />
          }
          panel={
            currentSection === Section.Fabric ? (
              <FabricPanel
                fabricList={fabricList}
                fabricSequence={fabricSequence}
                fabricFilters={fabricFilters}
                fabricPreFilters={preFilters}
                fabricViewMode={fabricViewMode}
                firstRenderRef={firstRenderRef}
                navigation={navigation}
                businessRules={businessRules}
                options={options}
                optionValues={optionValues}
                defaultValues={defaultValues}
                isInPreloadFlow={isInPreloadFlow}
                isCustomizeFlow={isCustomizeFlow}
                origin={origin}
              />
            ) : (
              <OptionPanel
                options={
                  options[
                    // To handle extra configurations which have different configuration type id but using definitions from base product
                    getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(
                      +getConfigurationTypeBySection(currentSection),
                    )
                  ]?.items
                }
                optionValues={optionValues}
                extraProductHeader={extraProductHeader}
                navigation={navigation}
                stylePresets={matchingProductPresets}
                businessRules={businessRules}
                layerDefinitions={layerDefinitions}
                defaultValues={defaultValues}
                isInPreloadFlow={isInPreloadFlow}
                fabricViewMode={fabricViewMode}
              />
            )
          }
          summary={
            <Suspense fallback={<Skeleton product={product} cdnDomain={cdnDomain} />}>
              <Summary
                layerDefinitions={layerDefinitions}
                optionValues={optionValues}
                options={options}
                fabricList={fabricList}
                stylePresets={stylePresets}
                isInPreloadFlow={isInPreloadFlow}
              />
            </Suspense>
          }
          modals={<ConfiguratorModals />}
        />
      )}
      <ConfiguratorToast />
    </>
  );
};
